import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { Typography } from '~/components/common/Typography'

export interface SequentialSelectStepItem {
  value: string
  label: string
  next?: SequentialSelectStep
}

export interface SequentialSelectStep {
  label: string
  itemsLabel: string
  items: SequentialSelectStepItem[]
  prev?: SequentialSelectStep
}

export interface SequentialSelectProps {
  step: SequentialSelectStep
  onComplete?: (selectedItems: SequentialSelectStepItem[]) => void
  placeholder?: string
  className?: string
  maxHeight?: string
  disabled?: boolean
}

export const SequentialSelect: React.FC<SequentialSelectProps> = ({
  step,
  onComplete,
  placeholder = 'Search or select...',
  className = '',
  maxHeight = 'max-h-64',
  disabled = false
}) => {
  // Outer wrapper — also doubles as the popover's positioning anchor so the
  // dropdown matches the wrapper's width and 4px gap (mirroring the original
  // `absolute left-0 right-0 top-full mt-1` behavior before we moved the
  // dropdown into a body-level portal).
  const containerRef = useRef<HTMLDivElement>(null)
  // Viewport-relative rect of the wrapper, used to position the portal'd
  // dropdown. We portal to <body> so the popover can overlay modal footers
  // and other ancestors with `overflow: hidden` / `overflow-y: auto`.
  const [popoverRect, setPopoverRect] = useState<{
    top: number
    left: number
    width: number
  } | null>(null)
  const [searchTerm, setSearchTerm] = useState('')
  const [selections, setSelections] = useState<SequentialSelectStepItem[]>([])
  // Completed sequences awaiting confirmation. Each entry is one full
  // step→step→… chain. Rendered as removable chips inside the search bar so
  // the user can stage multiple picks (e.g. several repo→branch selections)
  // before flushing them to the parent via the → button or input blur. We
  // mirror this in a ref so blur and Confirm handlers — which can fire
  // alongside another synchronous state update — read the latest value
  // without waiting for React to commit.
  const [committed, setCommitted] = useState<SequentialSelectStepItem[][]>([])
  const committedRef = useRef<SequentialSelectStepItem[][]>([])
  const [currStep, setCurrStep] = useState<SequentialSelectStep>(step)
  const [focused, setFocused] = useState(false)
  const [index, setIndex] = useState(0)
  const [inputRef, setInputRef] = useState<HTMLInputElement | null>(null)
  const onCompleteRef = useRef(onComplete)
  onCompleteRef.current = onComplete

  const focusInput = () => {
    if (disabled || !currStep) return
    inputRef?.focus()
    setFocused(true)
  }

  // Auto-focus on mount so the dropdown is open by default. The portal'd
  // popover starts at opacity 0 and fades in (see `popoverShown` below) so
  // it cross-fades with the host modal's own open transition instead of
  // popping in as a separate event.
  useEffect(() => {
    if (!inputRef) return
    inputRef.focus()
    setFocused(true)
  }, [inputRef])

  // Drive a CSS opacity transition for the portal'd dropdown. Mount happens
  // when `popoverRect` first lands; flipping `popoverShown` on the next
  // frame triggers the 0→1 fade so the popover slides in smoothly with the
  // modal animation rather than appearing as a hard pop.
  const [popoverShown, setPopoverShown] = useState(false)
  useLayoutEffect(() => {
    if (!popoverRect) {
      setPopoverShown(false)
      return
    }
    const raf = requestAnimationFrame(() => setPopoverShown(true))
    return () => cancelAnimationFrame(raf)
  }, [popoverRect])

  useEffect(() => {
    setSelections([])
    setCommitted([])
    committedRef.current = []
    setCurrStep(step)
    setIndex(0)
  }, [step])

  // Track the search bar's viewport position so the portal'd dropdown stays
  // glued to it. We poll via rAF while focused because the popover sits in
  // <body> and doesn't ride along when the modal animates open with a
  // transform — listening only to scroll/resize would leave the dropdown
  // floating at the modal's pre-animation position. The rAF loop short-
  // circuits when the rect hasn't changed, so it's idle in steady state.
  useLayoutEffect(() => {
    if (!focused) {
      setPopoverRect(null)
      return
    }
    let raf = 0
    let last: { top: number; left: number; width: number } | null = null
    const tick = () => {
      const el = containerRef.current
      if (el) {
        const rect = el.getBoundingClientRect()
        const next = { top: rect.bottom + 4, left: rect.left, width: rect.width }
        if (
          !last ||
          last.top !== next.top ||
          last.left !== next.left ||
          last.width !== next.width
        ) {
          last = next
          setPopoverRect(next)
        }
      }
      raf = requestAnimationFrame(tick)
    }
    tick()
    return () => {
      if (raf) cancelAnimationFrame(raf)
    }
  }, [focused])

  // Filter current step items based on search term, and — at the root step —
  // also hide any value already staged as a committed chip so the user can't
  // queue the same root entry twice (e.g. the same repo with two different
  // branches). Inner-step items (branches) aren't deduped here because they're
  // already scoped to the parent root pick.
  const usedRootValues =
    currStep === step
      ? new Set(committed.map(seq => seq[0]?.value).filter(Boolean) as string[])
      : null
  const filteredItems =
    currStep?.items.filter(item => {
      const matchesSearch =
        !searchTerm.trim() || item.label.toLowerCase().includes(searchTerm.toLowerCase())
      if (!matchesSearch) return false
      if (usedRootValues && usedRootValues.has(item.value)) return false
      return true
    }) || []

  const flushCommitted = useCallback(() => {
    const queued = committedRef.current
    if (queued.length === 0) return
    committedRef.current = []
    setCommitted([])
    // Fire one onComplete per fully-formed sequence. Functional updates in
    // consumers (e.g. setSelectedRepos(prev => [...prev, …])) batch correctly
    // so all picks land in a single parent re-render.
    for (const seq of queued) {
      onCompleteRef.current?.(seq)
    }
    setSelections([])
    setCurrStep(step)
    setIndex(0)
    setSearchTerm('')
  }, [step])

  // Handle item selection
  const handleItemClick = (item: SequentialSelectStepItem, e?: React.MouseEvent) => {
    e?.preventDefault()
    if (disabled || !currStep) return
    const newSelections = [...selections, item]
    setSearchTerm('')
    if (!item.next) {
      // Final step picked — stage as a committed chip and reset to step 1 so
      // the user can pick another sequence in the same session.
      const next = [...committedRef.current, newSelections]
      committedRef.current = next
      setCommitted(next)
      setSelections([])
      setCurrStep(step)
      setIndex(0)
    } else {
      setSelections(newSelections)
      setCurrStep(item.next)
      setIndex(index + 1)
    }
  }

  const handleConfirm = (e?: React.MouseEvent) => {
    e?.preventDefault()
    // Drop any in-progress sequence — Confirm only commits fully-formed
    // chips. A repo without a branch picked yet is intentionally discarded.
    setSelections([])
    setCurrStep(step)
    setIndex(0)
    setSearchTerm('')
    flushCommitted()
    // Keep input focused so the dropdown re-opens immediately at step 1.
    inputRef?.focus()
  }

  // Right-side clear button: undo the last in-progress selection, or — when
  // there's nothing in progress — pop the most recent committed chip.
  const handleClearLatest = (e: React.MouseEvent) => {
    e.preventDefault()
    if (selections.length > 0) {
      const newSelections = selections.slice(0, -1)
      setSelections(newSelections)
      setIndex(Math.max(0, index - 1))
      if (newSelections.length === 0) {
        setCurrStep(step)
      } else {
        const lastSelection = newSelections[newSelections.length - 1]
        if (lastSelection.next) {
          setCurrStep(lastSelection.next)
        }
      }
      return
    }
    if (committedRef.current.length > 0) {
      const next = committedRef.current.slice(0, -1)
      committedRef.current = next
      setCommitted(next)
    }
  }

  const handleRemoveCommitted = (i: number, e: React.MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()
    const next = committedRef.current.filter((_, idx) => idx !== i)
    committedRef.current = next
    setCommitted(next)
  }

  const handleBlur = () => {
    setFocused(false)
    // Commit staged chips when the user clicks off. In-progress (partial)
    // selections are intentionally dropped — they aren't full sequences and
    // the parent has nothing actionable to receive.
    if (committedRef.current.length > 0) flushCommitted()
  }

  return (
    <div
      ref={containerRef}
      className={`relative transition-all duration-200 py-2 px-4 ${
        focused ? 'bg-tokens-neutral-bg-white rounded-xl shadow-popover z-10' : ''
      } ${className}`}
    >
      {/* Search Input */}
      <div
        className='box-border flex flex-row items-center justify-between px-2 py-1.5 relative rounded-xl transition-all duration-200'
        onClick={focusInput}
      >
        <div
          className={`absolute border border-solid inset-0 pointer-events-none rounded-xl ${
            focused ? 'border-[#846fe1]' : 'border-tokens-neutral-border-1'
          }`}
        ></div>

        {/* Left side: Search icon + Committed chips + In-progress chip + Input */}
        <div className='flex flex-row flex-wrap items-center gap-2 flex-1 min-w-0'>
          <div className='shrink-0 size-4'>
            <svg
              width='16'
              height='16'
              viewBox='0 0 16 16'
              fill='none'
              xmlns='http://www.w3.org/2000/svg'
              className='size-full'
            >
              <path
                d='M7.25 12.5C10.1495 12.5 12.5 10.1495 12.5 7.25C12.5 4.35051 10.1495 2 7.25 2C4.35051 2 2 4.35051 2 7.25C2 10.1495 4.35051 12.5 7.25 12.5Z'
                className='stroke-tokens-neutral-icon-default'
                strokeWidth='1'
                strokeLinecap='round'
                strokeLinejoin='round'
              />
              <path
                d='M11.0742 11.0742L14.0002 14.0002'
                className='stroke-tokens-neutral-icon-default'
                strokeWidth='1'
                strokeLinecap='round'
                strokeLinejoin='round'
              />
            </svg>
          </div>

          {/* Committed chips — one per completed step→…→step sequence. */}
          {committed.map((seq, i) => {
            const chipLabel = seq.map(s => s.label).join(' / ')
            return (
              <div
                key={`committed-${i}-${seq.map(s => s.value).join('|')}`}
                data-testid={`sequential-select-committed-${i}`}
                className='box-border flex flex-row h-7 items-center gap-1 pl-3 pr-1 py-0 relative rounded-full shrink-0 select-none bg-tokens-primary-3 max-w-full'
              >
                <Typography
                  fontFamily='geist'
                  fontSize='bodySm'
                  fontWeight='medium'
                  className='text-tokens-status-link-3 truncate'
                >
                  {chipLabel}
                </Typography>
                <button
                  type='button'
                  data-testid={`sequential-select-committed-remove-${i}`}
                  onMouseDown={e => handleRemoveCommitted(i, e)}
                  className='shrink-0 size-4 flex items-center justify-center rounded-full text-tokens-status-link-3 hover:bg-tokens-status-link-3/15 transition-colors'
                  title={`Remove ${chipLabel}`}
                  aria-label={`Remove ${chipLabel}`}
                >
                  <svg
                    width='12'
                    height='12'
                    viewBox='0 0 12 12'
                    fill='none'
                    xmlns='http://www.w3.org/2000/svg'
                    aria-hidden
                  >
                    <path
                      d='M3 3L9 9M9 3L3 9'
                      stroke='currentColor'
                      strokeWidth='1.6'
                      strokeLinecap='round'
                      strokeLinejoin='round'
                    />
                  </svg>
                </button>
              </div>
            )
          })}

          {/* In-progress chip — visible mid-sequence (e.g. repo picked, branch
              still pending). Stays unfilled to read as tentative vs. the
              filled committed chips. */}
          {currStep.prev && selections.length > 0 && (
            <div className='box-border flex flex-row h-8 items-center justify-center p-0 relative rounded-[50px] shrink-0 select-none'>
              <div className='flex flex-row gap-1 items-center justify-center leading-[0] pl-3 pr-2 py-1 relative shrink-0 text-nowrap'>
                <div className='flex flex-col font-semibold justify-center relative shrink-0 text-[12px] text-tokens-neutral-text-tertiary text-left'>
                  <Typography fontFamily='geistMono' fontSize='labelSm' fontWeight='semibold'>
                    {currStep.prev.label}:
                  </Typography>
                </div>
                <div className='flex flex-col font-medium justify-center relative shrink-0 text-[14px] text-tokens-neutral-text-primary text-center'>
                  <Typography fontFamily='geist' fontSize='bodyMd' fontWeight='medium'>
                    {selections[selections.length - 1].label}
                  </Typography>
                </div>
              </div>
            </div>
          )}

          <input
            type='text'
            ref={setInputRef}
            placeholder={
              index === 0 && committed.length === 0 && selections.length === 0
                ? placeholder
                : undefined
            }
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
            onFocus={() => setFocused(true)}
            onBlur={handleBlur}
            disabled={disabled || !currStep}
            className='flex-1 bg-transparent border-none outline-none font-sans font-medium text-base placeholder:text-tokens-neutral-text-placeholder disabled:cursor-not-allowed py-1 min-w-0 select-text'
            style={{
              fontSize: '14px',
              lineHeight: '22px',
              textOverflow: 'ellipsis',
              overflow: 'hidden',
              whiteSpace: 'nowrap'
            }}
          />
        </div>

        {/* Right side: Clear-latest + Confirm. Both buttons stay visible
            whenever any selection exists (committed chips or in-progress)
            and pin to the top-right of the wrap area via self-start so they
            don't drift into the middle of a multi-row chip stack. */}
        {(selections.length > 0 || committed.length > 0) && (
          <div className='flex flex-row items-center gap-2 ml-2 shrink-0 self-start'>
            <button
              type='button'
              onMouseDown={e => handleClearLatest(e)}
              className='shrink-0 size-4 text-tokens-neutral-icon-default hover:text-tokens-neutral-icon-hover transition-colors'
              title='Clear latest selection'
            >
              <svg
                width='16'
                height='16'
                viewBox='0 0 16 16'
                fill='none'
                xmlns='http://www.w3.org/2000/svg'
              >
                <path
                  d='M8 1C4.13438 1 1 4.13438 1 8C1 11.8656 4.13438 15 8 15C11.8656 15 15 11.8656 15 8C15 4.13438 11.8656 1 8 1ZM10.5844 10.6594L9.55313 10.6547L8 8.80313L6.44844 10.6531L5.41563 10.6578C5.34688 10.6578 5.29063 10.6031 5.29063 10.5328C5.29063 10.5031 5.30156 10.475 5.32031 10.4516L7.35313 8.02969L5.32031 5.60938C5.30143 5.58647 5.29096 5.5578 5.29063 5.52812C5.29063 5.45937 5.34688 5.40312 5.41563 5.40312L6.44844 5.40781L8 7.25938L9.55156 5.40938L10.5828 5.40469C10.6516 5.40469 10.7078 5.45937 10.7078 5.52969C10.7078 5.55937 10.6969 5.5875 10.6781 5.61094L8.64844 8.03125L10.6797 10.4531C10.6984 10.4766 10.7094 10.5047 10.7094 10.5344C10.7094 10.6031 10.6531 10.6594 10.5844 10.6594Z'
                  fill='black'
                  fillOpacity='0.88'
                />
              </svg>
            </button>

            <button
              type='button'
              data-testid='sequential-select-confirm'
              onMouseDown={e => handleConfirm(e)}
              className='shrink-0 flex items-center justify-center h-7 w-7 rounded-full bg-tokens-neutral-text-primary text-white hover:opacity-90 transition-opacity'
              title='Confirm selection'
              aria-label='Confirm selection'
            >
              <svg
                width='14'
                height='14'
                viewBox='0 0 14 14'
                fill='none'
                xmlns='http://www.w3.org/2000/svg'
                aria-hidden
              >
                <path
                  d='M3 7H11M11 7L7 3M11 7L7 11'
                  stroke='currentColor'
                  strokeWidth='1.6'
                  strokeLinecap='round'
                  strokeLinejoin='round'
                />
              </svg>
            </button>
          </div>
        )}
      </div>

      {/* Dropdown Content — rendered via a portal to <body> with position:
          fixed so it overlays modal footers and any ancestor with
          `overflow: hidden` / `overflow-y: auto`. Stays open across
          multi-pick sessions so the user can stage several completed
          sequences as chips before confirming. */}
      {focused &&
        popoverRect &&
        createPortal(
          <div
            data-testid='sequential-select-dropdown'
            style={{
              position: 'fixed',
              top: `${popoverRect.top}px`,
              left: `${popoverRect.left}px`,
              width: `${popoverRect.width}px`,
              zIndex: 9999,
              opacity: popoverShown ? 1 : 0,
              transform: popoverShown ? 'translateY(0)' : 'translateY(-4px)',
              transition: 'opacity 160ms ease-out, transform 160ms ease-out'
            }}
            className='py-3 px-4 bg-tokens-neutral-bg-white rounded-xl shadow-popover'
          >
            {/* Current Step Header */}
            {currStep && (
              <div className='mb-3 px-2'>
                <Typography
                  fontFamily='geistMono'
                  fontSize='labelMd'
                  fontWeight='semibold'
                  className='text-tokens-neutral-text-secondary select-none'
                >
                  {currStep.itemsLabel}
                </Typography>
              </div>
            )}

            {/* Items List — wrapped in a positioned container so a soft fade
              gradient can soften the bottom cutoff when items overflow. */}
            <div className='relative'>
              <div className={`flex flex-col gap-2 ${maxHeight} overflow-y-auto`}>
                {filteredItems.map(item => (
                  <div
                    key={item.value}
                    onMouseDown={e => handleItemClick(item, e)}
                    className='flex flex-row h-8 items-center justify-start p-0 rounded-lg cursor-pointer transition-colors hover:bg-tokens-neutral-fill-fill-4'
                  >
                    {/* Item Name */}
                    <div
                      className='flex-1 min-w-0 py-[5px] px-[12px]'
                      data-testid={`sequential-select-item-${item.value}`}
                    >
                      <Typography
                        fontFamily='geist'
                        fontSize='bodyMd'
                        fontWeight='medium'
                        className='text-tokens-neutral-text-primary truncate select-none'
                      >
                        {item.label}
                      </Typography>
                    </div>
                  </div>
                ))}
                {filteredItems.length === 0 && currStep && (
                  <div className='flex items-center justify-center py-4'>
                    <Typography
                      fontFamily='geist'
                      fontSize='bodySm'
                      fontWeight='medium'
                      className='text-tokens-neutral-text-tertiary select-none'
                    >
                      No items found
                    </Typography>
                  </div>
                )}
              </div>
              {/* Soft bottom-fade so the last visible item dissolves into the
                card edge instead of getting hard-clipped when the list
                overflows. */}
              <div
                aria-hidden
                className='pointer-events-none absolute bottom-0 left-0 right-0 h-8 bg-gradient-to-t from-tokens-neutral-bg-white to-transparent'
              />
            </div>
          </div>,
          document.body
        )}
    </div>
  )
}

export default SequentialSelect
